import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {useParams} from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import {number, string, bool, func} from "prop-types";

import { Modal, TableRow } from "~/components";
import PopupViewer from "~/components/PopupViewer";
import PopupWrapper from "~/components/PopupWrapper";
import useItemMarkedFlag from "~/hooks/useItemMarkedFlag";

import { useResultData } from "../../../resultContext";
import { useSelectRisks } from "~/views/Search/Response/Diligence2/services/selectRisksContext";
import {useApi} from "~/hooks";
import {useAlert} from "react-alert";

const DefaultRiskItem = ({
  id,
  confirmed,
  coluna1,
  coluna2,
  coluna3,
  coluna4,
  popup,
  subjectPrefix,
  showToggleModal,
  showReasonModal,
  confirmed_change_reason,
  riskSourceID,
  themeID,
  hasTooltip,
  impactId,
  caseProgressStatus,
  caseProgressHandler
}) => {
  const popupWrapperRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => popupWrapperRef.current,
  });

  const [showPopup, setShowPopup] = useState(false);
  const { id: processID } = useParams();
  const { seem, entityNames } = useResultData();
  const { t } = useTranslation("diligence");
  const { markedFlagColumn } = useItemMarkedFlag(id);
  const { selectedRisks, setSelectedRisks, isProcess, isMedia } = useSelectRisks();
  const api = useApi();
  const alert = useAlert();
  
  const [isChecked, setIsChecked] = useState(false);

  const parsedPopup = useMemo(
    () => (popup ? JSON.parse(popup) : null),
    [popup]
  );

  const parseColumn = (data) => {
    if (data === null || data === undefined) return null;
    if (typeof data === "string") return data.trim() || null;
    if (typeof data === "object") return JSON.stringify(data);
    return String(data).trim() || null;
  };

  // Verifica se deve mostrar a bolinha de risco
  const shouldShowRiskDot =
    impactId &&
    impactId > 0 &&
    parsedPopup &&
    parsedPopup.type === "processo_judicial_view";

  const handleCheck = useCallback(
    (value, evt) => {
      if (evt) setSelectedRisks([...selectedRisks, value]);
      else setSelectedRisks(selectedRisks.filter((item) => item !== value));
    },
    [isChecked, selectedRisks, setSelectedRisks]
  );

  let checkboxColumn;
  if (isProcess(subjectPrefix) || isMedia(subjectPrefix)) {
    checkboxColumn = {
      type: "CHECKALL",
      payload: {
        value: { value: id, state: isChecked },
        action: handleCheck,
      },
    };
  } else {
    checkboxColumn = false;
  }

  useEffect(() => {
    setIsChecked(selectedRisks.includes(id));
  }, [selectedRisks]);

  return (
    <>
      <TableRow
        columns={[
          checkboxColumn,
          {
            payload: shouldShowRiskDot
              ? {
                  text: parseColumn(coluna1),
                  impactId,
                  showRiskDot: true,
                }
              : parseColumn(coluna1),
            type: shouldShowRiskDot ? "RISK_TEXT" : "TEXT",
          },
          {
            payload: parseColumn(coluna2),
            type: isProcess(subjectPrefix) || hasTooltip
              ? "TOOLTIP"
              : "TEXT",
          },
          {
            payload: parseColumn(coluna3),
            type: "LINKMEDIA",
          },
          {...(coluna4 &&
              {
                payload: parseColumn(coluna4),
                type: 'TEXT',
              }
          )},
          {
            type: "TOGGLE",
            payload: {
              value: confirmed,
              action: {
                toggle() {
                  if (seem?.data?.id) {
                    return alert.error(t("toggle.alerts.finalized"));
                  }

                  showToggleModal({
                    id,
                    themeId: themeID,
                    riskId: riskSourceID,
                    active: confirmed,
                    reason: confirmed_change_reason,
                  });
                },
                edit: confirmed_change_reason
                  ? () => {
                      showReasonModal({
                        id,
                        themeId: themeID,
                        riskId: riskSourceID,
                        active: confirmed,
                        reason: confirmed_change_reason,
                      });
                    }
                  : null,
              },
            },
          },
          markedFlagColumn,
          (() => {
            const types = [];
            if (popup && popup !== "null") {
              types.push("FILE");
            }
            
            if (subjectPrefix) {
              types.push("ADD_CLIPBOARD");
            }
            
            if (parsedPopup?.type === 'processo_judicial_view') {
              types.push("CASE_PROGRESS");
            }
            
            return {
              type: "BUTTONS",
              payload: {
                types,
                process_id: processID,
                result_id: id,
                caseProgressStatus,
                caseProgressAction: {
                  onClick: () => caseProgressHandler(caseProgressStatus, processID, coluna2, riskSourceID)
                },
                subject: subjectPrefix
                  ? `${subjectPrefix} - ${parseColumn(coluna1)}`
                  : null,
                fileAction: {
                  onClick: () => {
                    if (parsedPopup?.type !== "link") {
                      setShowPopup(true);
                    } else {
                      window.open(parsedPopup.href);
                    }
                  },
                },
              },
            };
          })(),
        ]}
      />
      {showPopup && !!parsedPopup && (
        <Modal
          show
          title={(() => {
            if (
              parsedPopup.type === "processo_judicial" ||
              parsedPopup.type === "processo_judicial_view"
            ) {
              return [entityNames[0], parsedPopup?.data?.Number].join(
                " - Processo: "
              );
            } else if (parsedPopup.type === "legacySurvey") {
              return [coluna1];
            }
            return [coluna1, coluna2].join(" - ");
          })()}
          onShow={setShowPopup}
          hasActions={false}
          size="lg"
          hasPrinterIcon={parsedPopup.type !== "pdf"}
          onClickPrinter={handlePrint}
        >
          <PopupWrapper ref={popupWrapperRef}>
            <PopupViewer popup={parsedPopup} />
          </PopupWrapper>
        </Modal>
      )}
    </>
  );
};

DefaultRiskItem.propTypes = {
  id: number.isRequired,
  confirmed: bool.isRequired,
  coluna1: string,
  coluna2: string,
  coluna3: string,
  coluna4: string,
  popup: string,
  subjectPrefix: string,
  showToggleModal: func.isRequired,
  showReasonModal: func.isRequired,
  confirmed_change_reason: string,
  riskSourceID: number.isRequired,
  themeID: number.isRequired,
  hasTooltip: bool,
  impactId: number,
  caseProgressStatus: string,
  caseProgressHandler: func,
};

DefaultRiskItem.defaultProps = {
  coluna1: '-',
  coluna2: '-',
  coluna3: '-',
  coluna4: null,
  popup: null,
  confirmed_change_reason: null,
  subjectPrefix: null,
  hasTooltip: false,
  impactId: null,
  caseProgressStatus: null,
  caseProgressHandler: null,
};

export default DefaultRiskItem;
 